import { shallow, useSelector } from '@tanstack/react-store';
import type { RowData, Cell, Column, Header } from '@tanstack/react-table';
import { type JSX, useCallback, useMemo, Children } from 'react';
import { createPortal } from 'react-dom';

import { useCloseFloatingMenuOnScroll } from './useCloseFloatingMenuOnScroll.js';
import * as styles from './UserActions.sty.js';
import { Menu } from '../../../../navigation/menu/Menu.js';
import {
  DataTableCellContextProvider,
  DataTableColumnContextProvider,
  DataTableHeaderContextProvider,
  useDataTable,
} from '../../contexts/DataTableContext.js';
import type { TDataTableFeatures } from '../types/features.types.js';

/**
 * @internal
 * Floating menu for right click events.
 */
export function CellActionFloatingMenu(): JSX.Element | undefined {
  const table = useDataTable();
  // Subscribe only to the floating menu state needed to position and render it.
  const { type, headerId, rowId, columnId, x, y } = useSelector(
    table.atoms.floatingMenuInfo,
    (floatingMenuInfo) =>
      floatingMenuInfo ?? {
        type: null,
        headerId: null,
        rowId: null,
        columnId: null,
        x: 0,
        y: 0,
      },
    { compare: shallow },
  );

  const actions = useMemo(() => {
    if (type === 'headerAction' && columnId !== null) {
      return table.getUserActionsForColumn(columnId);
    }

    if (type === 'cellAction' && columnId !== null && rowId !== null) {
      return table.getUserActionsForCell(columnId, rowId);
    }

    return null;
    // Avoid adding the `table` instance into the dependency array to prevent unnecessary re-renders
    // when the table instance changes but the relevant state does not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowId, columnId, type]);

  const menuOpen = type !== null && columnId !== null && actions !== null;

  const closeMenu = useCallback(() => {
    table.baseAtoms.floatingMenuInfo.set(() => ({
      type: null,
      headerId: null,
      rowId: null,
      columnId: null,
      x: 0,
      y: 0,
    }));
    table.baseAtoms.openMenuRowId.set(null);
  }, [table]);

  useCloseFloatingMenuOnScroll({
    isMenuOpen: menuOpen,
    closeMenu,
    stickyVertically: type === 'headerAction',
  });

  // Find the right column and cell objects to provide
  const { header, column, cell } = useMemo<{
    header?: Header<TDataTableFeatures, RowData>;
    column?: Column<TDataTableFeatures, RowData>;
    cell?: Cell<TDataTableFeatures, RowData>;
  }>(() => {
    if (menuOpen === false) {
      return { column: undefined, cell: undefined };
    }

    const header = headerId ? table.getHeader(headerId) : undefined;
    const column = table.getColumn(columnId);

    let cell: Cell<TDataTableFeatures, RowData> | undefined = undefined;
    if (rowId) {
      const rowCells = table.getRow(rowId).getAllCells();
      const columnCell = rowCells.find(
        (cell) => cell.column.id === columnId,
      ) as Cell<TDataTableFeatures, RowData> | undefined;

      cell = columnCell;
    }

    return { header, column, cell };
    // Avoid adding the `table` instance into the dependency array to prevent unnecessary re-renders
    // when the table instance changes but the relevant state does not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnId, headerId, menuOpen, rowId]);

  // Avoid spawning the menu while it is closed to avoid react render
  // performance issues.
  if (menuOpen === false) {
    return;
  }

  // We need to portal the entire floating menu to escape CSS transforms on ancestor elements.
  // This ensures transforms on parent containers don't affect the position of the trigger button.
  return createPortal(
    <Menu
      open={menuOpen}
      onOpenChange={(isOpen) => {
        if (isOpen === false) {
          closeMenu();
        }
      }}
      modal={false}
    >
      <Menu.Trigger>
        <button
          disabled
          type="button"
          className={styles.floatingMenuButton}
          style={{
            top: `${y}px`,
            left: `${x}px`,
          }}
          data-trigger="context-action"
        />
      </Menu.Trigger>
      {menuOpen === true && (
        <DataTableHeaderContextProvider value={header}>
          <DataTableColumnContextProvider value={column}>
            <DataTableCellContextProvider value={cell}>
              <Menu.Content>
                {/*
                 * Run all actions through a React.Children map so they will receive
                 * an automatic key and do not violate react key map rules.
                 */}
                {Children.map(actions, (child) => child)}
              </Menu.Content>
            </DataTableCellContextProvider>
          </DataTableColumnContextProvider>
        </DataTableHeaderContextProvider>
      )}
    </Menu>,
    document.body,
  );
}
